import React from "react";
import { Lock, Users, UserPlus } from "lucide-react";
import { T } from "../theme";

/**
 * PrivacyGate — glassmorphism gate card shown when a profile section is
 * restricted for the current viewer.
 *
 *   <PrivacyGate visibility="private" | "friends" isFriend={bool} onAddFriend={fn} />
 *
 * private       → lock message only
 * friends       → friends-only message + Send Friend Request CTA when the
 *                 viewer isn't a friend yet (requestState controls the CTA:
 *                 "none" | "pending" | "sent")
 */

function PrivacyGate({ visibility = "private", isFriend = false, onAddFriend, requestState = "none" }) {
  const isPrivate = visibility === "private";
  const Icon = isPrivate ? Lock : Users;
  const accent = isPrivate ? T.purple : T.pink;
  const showCta = !isPrivate && !isFriend && typeof onAddFriend === "function";
  const pending = requestState === "pending" || requestState === "sent";

  return (
    <div style={{
      position: "relative",
      background: T.card,
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: T.border,
      borderRadius: 24,
      padding: "48px 28px",
      textAlign: "center",
      overflow: "hidden",
    }}>
      {/* Ambient floating glow orbs */}
      <div className="animate-float" aria-hidden style={{
        position: "absolute", top: -40, left: "12%",
        width: 150, height: 150, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}22 0%, transparent 70%)`,
        pointerEvents: "none",
      }} />
      <div className="animate-float" aria-hidden style={{
        position: "absolute", bottom: -50, right: "10%",
        width: 180, height: 180, borderRadius: "50%",
        background: `radial-gradient(circle, ${T.cyan}18 0%, transparent 70%)`,
        pointerEvents: "none",
        animationDelay: "1.4s",
      }} />

      {/* Icon medallion */}
      <div style={{
        position: "relative",
        width: 72, height: 72, borderRadius: "50%",
        margin: "0 auto 18px",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: `linear-gradient(135deg, ${accent}22, rgba(255,255,255,0.04))`,
        border: `1.5px solid ${accent}55`,
        boxShadow: `0 0 28px ${accent}33`,
      }}>
        <Icon size={28} color={accent} style={{ filter: `drop-shadow(0 0 8px ${accent})` }} />
      </div>

      <p style={{
        position: "relative", margin: "0 0 8px",
        color: T.textPrimary, fontWeight: 800, fontSize: "1.05rem",
      }}>
        {isPrivate ? "This stage is private 🔒" : "This stage is friends only 🤝"}
      </p>
      <p style={{
        position: "relative", margin: "0 auto",
        color: T.textMuted, fontSize: "0.88rem", maxWidth: 320, lineHeight: 1.5,
      }}>
        {isPrivate
          ? "This raver keeps their stage to themselves."
          : "Become friends to see their kandi, stage and story."}
      </p>

      {showCta && (
        <button
          onClick={pending ? undefined : onAddFriend}
          disabled={pending}
          style={{
            position: "relative",
            display: "inline-flex", alignItems: "center", gap: 8,
            marginTop: 22, padding: "13px 26px", minHeight: 44,
            borderRadius: 50, border: "none",
            background: pending
              ? "rgba(255,255,255,0.08)"
              : "linear-gradient(135deg, #ff3cac, #b44fff)",
            color: pending ? T.textMuted : "#fff",
            fontWeight: 700, fontSize: "0.9rem",
            cursor: pending ? "default" : "pointer",
            boxShadow: pending ? "none" : "0 4px 20px rgba(180,79,255,0.35)",
            touchAction: "manipulation",
          }}
        >
          <UserPlus size={16} />
          {pending ? "Request Pending" : "Send Friend Request"}
        </button>
      )}
    </div>
  );
}

export default PrivacyGate;
